/** Shapes of the skills API (src/… of the service is the authority). */

export type NodeStatus =
  'not_due' | 'unknown' | 'not_yet' | 'emerging' | 'reported' | 'achieved' | 'lost';
export type ActorRole = 'guardian' | 'self' | 'practitioner' | 'reporter' | 'system';
export type Setting = 'home' | 'clinic' | 'school' | 'app' | 'other';

export interface MapBranch {
  key: string;
  order: number;
  title: string;
  titleEn: string;
  summary: string;
  destination: string;
  frameworks: string[];
  nodeCount: number;
}
export interface MapView {
  version: number;
  branches: MapBranch[];
}

export interface NodeSummary {
  key: string;
  label: string;
  branch: string;
  sequence: number;
  kind: 'gate' | 'ladder' | 'profile';
  title: string;
  titleEn: string;
  criterion: string;
  typicalAge?: { fromMonths: number; toMonths: number };
  practiceCount: number;
}
export interface BranchView {
  branch: Omit<MapBranch, 'nodeCount'>;
  nodes: NodeSummary[];
}

export interface Practice {
  key: string;
  title: string;
  setting: 'clinic' | 'home' | 'both';
  how: string;
  dose?: string;
  approach?: string;
}
export interface NodeFull extends NodeSummary {
  concept: string;
  acceptance: {
    behavior: string;
    threshold: string;
    independence: string;
    generalization: string;
    stability: string;
    confirmedBy: string;
    emergingMeans: string;
  };
  evidence: { source: string; question?: string; name?: string; how?: string; pass?: string }[];
  practices: Practice[];
}

export interface ProfileCard {
  id: string;
  homeRegion: string;
  displayName: string;
  dateOfBirth: string;
  ageMonths: number;
  myRole: ActorRole;
  myLevel: string;
  myBranches: string[] | null;
  ownerKind: 'guardian' | 'self';
}

export interface BranchSummary {
  branch: string;
  title: string;
  order: number;
  currentNode: string | null;
  currentTitle: string | null;
  nextNode: string | null;
  nextTitle: string | null;
  expectedNode: string | null;
  expectedTitle: string | null;
  ageGap: number | null;
  counts: Record<NodeStatus, number>;
  stepCount: number;
}

export interface ProfileOverview extends ProfileCard {
  mapVersion: number;
  policy: {
    selfAppFromMonths: number;
    adultFromMonths: number;
    reason: string | null;
    selfAppDue: boolean;
    adultDue: boolean;
  };
  branches: BranchSummary[];
  members: { id: string; uid: string; role: string; label: string | null }[];
  grants: {
    id: string;
    label: string | null;
    level: string;
    branches: string[];
    granteeUid: string;
  }[];
  eventCount: number;
}

export interface NodeState extends NodeSummary {
  status: NodeStatus;
  confirmedRole: ActorRole | null;
  reportedBy: ActorRole[];
  lastEventAt: string | null;
  practiceCount: number;
}
export interface StateView {
  asOf: string | null;
  ageMonths: number;
  nodes: NodeState[];
}

export interface TimelineEntry {
  seq: number;
  type: string;
  at: string;
  recordedAt: string;
  actorRole: ActorRole;
  actorLabel: string | null;
  nodeKey: string | null;
  nodeLabel: string | null;
  branchKey: string | null;
  setting: string | null;
  summary: string;
  payload: Record<string, unknown>;
}
export interface TimelinePage {
  entries: TimelineEntry[];
  nextBefore: number | null;
}

export interface PlanItem {
  id: string;
  nodeKey: string;
  nodeLabel: string;
  nodeTitle: string;
  branchKey: string;
  practiceKey: string | null;
  title: string;
  how: string | null;
  dose: string | null;
  setting: Setting;
  timesPerWeek: number | null;
  note: string | null;
  assignedRole: string;
  active: boolean;
  createdAt: string;
  endedAt: string | null;
  doneThisWeek: number;
  nodeStatus: NodeStatus;
}

export interface TodayView {
  ageMonths: number;
  practices: PlanItem[];
  nextSteps: {
    branchKey: string;
    branchTitle: string;
    nodeKey: string;
    nodeLabel: string;
    nodeTitle: string;
    status: NodeStatus;
  }[];
  recentlyAchieved: { nodeKey: string; nodeLabel: string; nodeTitle: string; at: string }[];
}

export interface GrantView {
  id: string;
  granteeUid: string;
  granteeKind: string;
  level: string;
  branches: string[];
  label: string | null;
  startsAt: string;
  endsAt: string | null;
  revokedAt: string | null;
  active: boolean;
}

export const STATUS_FA: Record<NodeStatus, string> = {
  achieved: 'یاد گرفته',
  reported: 'شما دیده‌اید',
  emerging: 'در حال یادگیری',
  not_yet: 'هنوز نه',
  unknown: 'نمی‌دانیم',
  not_due: 'بعداً',
  lost: 'از دست رفته',
};

export const SETTING_FA: Record<Setting, string> = {
  home: 'خانه',
  clinic: 'جلسه',
  school: 'مدرسه',
  app: 'اپ',
  other: 'جای دیگر',
};
